import createCommunityPost from '@dehaat/kisan-app-bl/api/createCommunityPost';
import fetchAllCropsV2, { FETCH_CROPS } from '@dehaat/kisan-app-bl/api/fetchAllCropsV2';
import { DEFAULT_LANG_NAME } from '@dehaat/kisan-app-bl/constants/common';
import Crop, { CropsWithCategoryProps } from '@dehaat/kisan-app-bl/models/CropV2';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import AddSowingDate from '@/components/askQuestion/AddSowingDate';
import ChooseCrop from '@/components/askQuestion/ChooseCrop';
import DiseaseUpload from '@/components/askQuestion/DiseaseUpload';
import PostQuestion from '@/components/askQuestion/PostQuestion';
import SuccessPopup from '@/components/askQuestion/SuccessPopup';
import ErrorModal from '@/components/ErrorModal';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import RightIcon from '@/components/icons/RightIcon';
import LoginScreen from '@/components/login/LoginScreen';
import Okay from '@/components/modalActions/Okay';
import Spinner from '@/components/Spinner';
import { SHOW_SOWING_DATE } from '@/constants/common';
import { AuthContext } from '@/context/AuthProvider';
import useErrorModalState from '@/hooks/useErrorModalState';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
interface CrumbProps {
  isCompleted: boolean;
  isHighlighted: boolean;
}
const Crumb = ({
  isCompleted,
  isHighlighted
}: CrumbProps) => {
  return <div className={`border h-5 w-5 rounded-full ${isHighlighted ? 'border-primary-100' : 'border-neutral-40'}`} data-sentry-component="Crumb" data-sentry-source-file="ask-question.tsx">
      <div className={`border border-white h-4.5 px-0.5 pt-1 rounded-full w-4.5 ${isHighlighted ? 'bg-primary-100' : 'bg-white'}`}>
        {isCompleted && <RightIcon className="fill-white stroke-white" width={12} />}
      </div>
    </div>;
};
const headings = ['take_photo', 'choose_crop', 'ask_your_question', 'enter_details'];
const AskQuestionPage = () => {
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const {
    errorModalState,
    hideErrorModal,
    setErrorModalState
  } = useErrorModalState();
  const {
    locale
  } = useRouter();
  const [categorizedCrops, setCategorizedCrops] = useState<CropsWithCategoryProps[]>([]);
  const [cropId, setCropId] = useState(NaN);
  const [currentCrumbIndex, setCurrentCrumbIndex] = useState(0);
  const [diseaseImages, setDiseaseImages] = useState<Blob[]>([]);
  const [isPosting, setIsPosting] = useState(false);
  const [postSlug, setPostSlug] = useState('');
  const [question, setQuestion] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const {
    t
  } = useTranslation('askQuestion');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  let selectedCrop: Crop | undefined;
  for (const category of categorizedCrops) {
    for (const crop of category.crops) {
      if (cropId === crop.id) {
        selectedCrop = crop;
      }
    }
  }
  const onSubmit = async (hasUsedMedicine = false) => {
    setIsPosting(true);
    const communityPost = await createCommunityPost(locale as keyof typeof DEFAULT_LANG_NAME, `${question}${hasUsedMedicine ? ` ${t('medicine')} ${t('common:yes')}` : ''}`, [], diseaseImages);
    setIsPosting(false);
    if (communityPost) {
      setPostSlug(communityPost.slug);
    } else {
      setErrorModalState({
        ...errorModalState,
        show: true
      });
    }
  };
  const toggleLoginModal = useCallback(() => setShowLogin(prev => !prev), []);
  useEffect(() => {
    // Different crop categories are received for (non)logged-in users
    // Also, fetchAllCropsV2 behaves a little differently for kheti vs server config
    // Until the configs are sorted out, fetch the crops on client side
    // instead of getServerSideProps
    // Tracked in FSS-1600
    const getCategorizedCrops = async () => {
      setCategorizedCrops((await fetchAllCropsV2(locale, FETCH_CROPS.ALL_CROPS)) || []);
    };
    getCategorizedCrops();
  }, [locale]);
  useEffect(() => {
    // Reset the cropId to NaN if user goes back
    if (currentCrumbIndex < 1) {
      setCropId(NaN);
    }
  }, [currentCrumbIndex]);
  useEffect(() => {
    trackCustomEvent(CustomEventTypes.ASK_HELP_USER_LANDED_ON_DISEASE_QUESTION_PAGE);
  }, []);
  return <main className="bg-neutral-10 min-h-screen" data-sentry-component="AskQuestionPage" data-sentry-source-file="ask-question.tsx">
      <HeaderWithBackButton heading={t(headings[currentCrumbIndex])} onBackClick={currentCrumbIndex === 0 ? undefined : () => setCurrentCrumbIndex(prev => prev - 1)} data-sentry-element="HeaderWithBackButton" data-sentry-source-file="ask-question.tsx" />
      <section className={`flex flex-col m-auto max-w-7xl min-h-screen pt-16 w-full ${currentCrumbIndex > 1 ? 'pb-20' : ''}`}>
        <div className="bg-white flex items-center pt-4 px-8">
          {Array(SHOW_SOWING_DATE ? 4 : 3).fill(null).map((_, index) => <React.Fragment key={index}>
                {index !== 0 && <div className="border-t border-neutral-40 grow" />}
                <Crumb isCompleted={index < currentCrumbIndex} isHighlighted={index <= currentCrumbIndex} />
              </React.Fragment>)}
        </div>
        {currentCrumbIndex === 0 && <DiseaseUpload setCurrentCrumbIndex={setCurrentCrumbIndex} setDiseaseImages={setDiseaseImages} />}
        {currentCrumbIndex === 1 && <ChooseCrop categorizedCrops={categorizedCrops} crops={[cropId]} onClick={id => setCropId(id)} setCurrentCrumbIndex={setCurrentCrumbIndex} />}
        {currentCrumbIndex === 2 && <PostQuestion crop={selectedCrop!} diseaseImages={diseaseImages} question={question} onSubmit={isAuthenticated ? onSubmit : toggleLoginModal} setCurrentCrumbIndex={setCurrentCrumbIndex} setQuestion={setQuestion} />}
        {SHOW_SOWING_DATE && currentCrumbIndex === 3 && <AddSowingDate crop={selectedCrop!} onSubmit={isAuthenticated ? onSubmit : toggleLoginModal} />}
        {isPosting && <Spinner />}
        <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-source-file="ask-question.tsx">
          <Okay handleClick={hideErrorModal} data-sentry-element="Okay" data-sentry-source-file="ask-question.tsx" />
        </ErrorModal>
        {postSlug && <SuccessPopup postSlug={postSlug} readMoreHref={`/crop/${cropId}/problems`} />}
        {showLogin && <LoginScreen isPopup onBack={toggleLoginModal} onLogin={toggleLoginModal} />}
      </section>
    </main>;
};
export const getServerSideProps: GetServerSideProps<{
  title: string;
}> = async () => ({
  props: {
    title: 'Ask Question'
  }
});
export default AskQuestionPage;